export const NEUTRAL_TRADE_RATE = '1';

const SAME_CURRENCY_AMOUNT_MISMATCH_MESSAGE =
  'Same currency amount mismatch';

const SAME_CURRENCY_AMOUNT_CONSISTENCY_TEST_NAME =
  'same-currency-amount-consistency';

// Relative epsilon, only wide enough to absorb IEEE-754 artifacts coming from
// `round()` (which returns a `toFixed` string) and from AutoNumeric parsing.
// Real decimal differences (1400.05 vs 1400.06) stay a mismatch.
const AMOUNT_EPSILON = 1e-9;

const PERSIAN_ZERO_CODE = 0x06f0;
const ARABIC_INDIC_ZERO_CODE = 0x0660;

// Group separators AutoNumeric may emit (comma, any whitespace, apostrophe),
// plus the Arabic thousands separator (U+066C) and the Arabic comma (U+060C).
const GROUP_SEPARATORS_REGEX = /[,\s'٬،]/g;
// Arabic decimal separator (U+066B).
const ARABIC_DECIMAL_SEPARATOR_REGEX = /٫/g;
// Persian (U+06F0..U+06F9) and Arabic-Indic (U+0660..U+0669) digits.
const NON_LATIN_DIGITS_REGEX = /[۰-۹٠-٩]/g;

const normalizeDigits = (value) =>
  value.replace(NON_LATIN_DIGITS_REGEX, (digit) => {
    const code = digit.charCodeAt(0);
    const zero =
      code >= PERSIAN_ZERO_CODE ? PERSIAN_ZERO_CODE : ARABIC_INDIC_ZERO_CODE;
    return String(code - zero);
  });

/**
 * Turns a triplex amount (or rate) into a finite number.
 *
 * Values reach the form as numbers (`event.numberValue`), as numeric strings
 * produced by `round()`, as AutoNumeric formatted strings ('1,400.00'), or as
 * empty placeholders while the user is still typing.
 *
 * @returns {number|null} `null` for empty / non numeric values, so callers can
 * leave the "is it filled?" question to the required / min rules.
 */
const parseAmountValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;

  const normalized = normalizeDigits(value.trim())
    .replace(ARABIC_DECIMAL_SEPARATOR_REGEX, '.')
    .replace(GROUP_SEPARATORS_REGEX, '');

  if (normalized === '' || normalized === '-' || normalized === '.') return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Amount equality that tolerates float noise but not real decimal drift. */
const areAmountsEquivalent = (first, second) => {
  const difference = Math.abs(first - second);
  if (difference === 0) return true;
  return (
    difference <=
    AMOUNT_EPSILON * Math.max(1, Math.abs(first), Math.abs(second))
  );
};

/**
 * `true` when both codes are filled and identical.
 *
 * Two missing codes are NOT a pair: the currencies are simply not chosen yet,
 * and nothing can be derived from them.
 */
export const isSameCurrencyPair = (
  initialCurrencyCode,
  equivalentCurrencyCode
) => {
  if (!initialCurrencyCode || !equivalentCurrencyCode) return false;
  return String(initialCurrencyCode) === String(equivalentCurrencyCode);
};

/**
 * `true` for a rate that means "no conversion", whether it was written by
 * `./utils.js` as the string `'1'`, typed into the masked input as the number
 * `1`, or loaded from the API as `1.00`.
 *
 * This only ever WIDENS an existing `=== '1'` check; the value written back is
 * still `NEUTRAL_TRADE_RATE`, so nothing that compares against the string
 * changes meaning.
 */
export const isNeutralTradeRate = (tradeRate) =>
  parseAmountValue(tradeRate) === 1;

/**
 * Whether the two amount fields already hold the same money.
 *
 * Unlike `isSameCurrencyAmountMismatch` this treats "one side empty" as out of
 * sync, because the component has to mirror an emptied field onto its twin.
 */
const areAmountValuesInSync = (
  initialAmountValue,
  equivalentAmountValue
) => {
  const initial = parseAmountValue(initialAmountValue);
  const equivalent = parseAmountValue(equivalentAmountValue);

  if (initial === null && equivalent === null) return true;
  if (initial === null || equivalent === null) return false;

  return areAmountsEquivalent(initial, equivalent);
};

/**
 * The submit-time question: is this pair impossible?
 *
 * @param {object} pair triplex values, in triplex vocabulary.
 * @returns {boolean} `true` only when both currency codes are filled and equal,
 * both amounts are filled and numeric, and the amounts are not the same money.
 * Empty amounts stay the business of the required / `min(0)` rules.
 */
export const isSameCurrencyAmountMismatch = ({
  initialCurrencyCode,
  initialAmountValue,
  equivalentCurrencyCode,
  equivalentAmountValue
} = {}) => {
  if (!isSameCurrencyPair(initialCurrencyCode, equivalentCurrencyCode)) {
    return false;
  }

  const initial = parseAmountValue(initialAmountValue);
  const equivalent = parseAmountValue(equivalentAmountValue);

  if (initial === null || equivalent === null) return false;

  return !areAmountsEquivalent(initial, equivalent);
};

/**
 * The pure half of the component fix: what has to change for a same-currency
 * pair to be consistent.
 *
 * @param {string} lockedLabel the DERIVED field ('initial' | 'equivalent' |
 * 'tradeRate'), i.e. the one the user is not editing.
 * @param {string[]} writableAmounts which of the two amounts the consumer
 * actually gave a setter for. Consumers such as draft conduction hand the
 * initial amount over as a read-only value taken from the order and pass no
 * `amountValueOnChange` for it.
 * @returns {object|null} `null` when the currencies are not a same-currency
 * pair (the caller must then run its normal conversion), otherwise the -
 * possibly empty - set of updates needed: `{ tradeRate }` and
 * `{ mirror: { target, value } }`.
 */
export const resolveSameCurrencyTriplexUpdate = ({
  initialCurrencyCode,
  initialAmountValue,
  equivalentCurrencyCode,
  equivalentAmountValue,
  tradeRate,
  lockedLabel,
  writableAmounts = ['initial', 'equivalent']
}) => {
  if (!isSameCurrencyPair(initialCurrencyCode, equivalentCurrencyCode)) {
    return null;
  }

  const update = {};

  if (!isNeutralTradeRate(tradeRate)) {
    update.tradeRate = NEUTRAL_TRADE_RATE;
  }

  if (!areAmountValuesInSync(initialAmountValue, equivalentAmountValue)) {
    // Write to the derived side, never to the one being edited. That is the
    // direction `./utils.js` already uses: locking 'initial' derives the
    // initial amount from the equivalent one, every other lock derives the
    // equivalent amount from the initial one.
    const preferredTarget = lockedLabel === 'initial' ? 'initial' : 'equivalent';
    const target = writableAmounts.includes(preferredTarget)
      ? preferredTarget
      : writableAmounts.find(
          (candidate) => candidate === 'initial' || candidate === 'equivalent'
        );

    if (target) {
      const source =
        target === 'initial' ? equivalentAmountValue : initialAmountValue;
      // `|| ''` is what `./utils.js` writes: clearing one amount clears its
      // twin instead of leaving a stale number behind.
      update.mirror = { target, value: source || '' };
    }
  }

  return update;
};

/**
 * Applies `resolveSameCurrencyTriplexUpdate` through the consumer supplied
 * Formik callbacks.
 *
 * @returns {boolean} `true` when the pair was a same-currency pair and has been
 * handled, so the caller can skip the conversion branches entirely.
 */
export const enforceSameCurrencyTriplexConsistency = ({
  initialCurrencyCode,
  initialAmountValue,
  equivalentCurrencyCode,
  equivalentAmountValue,
  tradeRate,
  lockedLabel,
  setValues
}) => {
  const {
    tradeRateOnChange,
    initialAmountValueOnChange,
    equivalentAmountValueOnChange
  } = setValues || {};

  const update = resolveSameCurrencyTriplexUpdate({
    initialCurrencyCode,
    initialAmountValue,
    equivalentCurrencyCode,
    equivalentAmountValue,
    tradeRate,
    lockedLabel,
    writableAmounts: [
      typeof initialAmountValueOnChange === 'function' && 'initial',
      typeof equivalentAmountValueOnChange === 'function' && 'equivalent'
    ].filter(Boolean)
  });

  if (!update) return false;

  if (
    update.tradeRate !== undefined &&
    typeof tradeRateOnChange === 'function'
  ) {
    tradeRateOnChange(update.tradeRate);
  }

  if (update.mirror) {
    const onChange =
      update.mirror.target === 'initial'
        ? initialAmountValueOnChange
        : equivalentAmountValueOnChange;
    onChange(update.mirror.value);
  }

  return true;
};

/**
 * Yup test descriptor asserting the same rule on submitted form values.
 *
 * The component keeps the pair in sync while it is mounted; this is the last
 * line of defence for state that was never re-synced (values restored into the
 * form by an async handler, edit-mode data, a currency changed while a value
 * was still being typed).
 *
 * A plain object descriptor, so this module still imports nothing - Yup binds
 * `this.createError` when it runs the test.
 *
 * @param {Function} t translation function.
 * @param {string} errorPath full path of the field the user can fix.
 * @param {string} name unique test name; defaults to one per amount field so
 * that two pairs can be attached to the same object schema (Yup keys tests by
 * name and the second would otherwise replace the first).
 */
export const sameCurrencyAmountConsistencyTest = ({
  t,
  initialCurrencyField,
  initialAmountField,
  equivalentCurrencyField,
  equivalentAmountField,
  errorPath,
  name = `${SAME_CURRENCY_AMOUNT_CONSISTENCY_TEST_NAME}-${equivalentAmountField}`
}) => ({
  name,
  message: t(SAME_CURRENCY_AMOUNT_MISMATCH_MESSAGE),
  test(values) {
    const mismatch = isSameCurrencyAmountMismatch({
      initialCurrencyCode: values?.[initialCurrencyField],
      initialAmountValue: values?.[initialAmountField],
      equivalentCurrencyCode: values?.[equivalentCurrencyField],
      equivalentAmountValue: values?.[equivalentAmountField]
    });

    if (!mismatch) return true;

    return this.createError({
      path: errorPath,
      message: t(SAME_CURRENCY_AMOUNT_MISMATCH_MESSAGE)
    });
  }
});
